import type {
  LightStudyGroupResponse,
  StudyGroupUnreadTotals,
} from "@/lib/humhub-api/types";

type StudyGroupUnreadGroupLike = Pick<
  LightStudyGroupResponse,
  "unread_post_comment_count" | "unread_mention_count"
>;

type StudyGroupUnreadTotalsResponseLike = {
  unread_post_comment_count_total?: number | null;
  unread_mention_count_total?: number | null;
};

type StudyGroupChannelUnreadLike = {
  channel_id: number | null;
  unread_post_comment_count?: number;
  unread_mention_count?: number;
};

type StudyGroupCatchUpUnreadLike = {
  isMention?: boolean;
};

type OffscreenUnreadGroupRect = {
  hasUnread: boolean;
  top: number;
  bottom: number;
};

type OffscreenUnreadViewport = {
  top: number;
  bottom: number;
};

export const EMPTY_STUDY_GROUP_UNREAD_TOTALS: StudyGroupUnreadTotals = {
  unreadPostCommentCount: 0,
  unreadMentions: 0,
};

export const toStudyGroupUnreadTotalsFromGroup = (
  group?: StudyGroupUnreadGroupLike | null,
): StudyGroupUnreadTotals => ({
  unreadPostCommentCount: group?.unread_post_comment_count ?? 0,
  unreadMentions: group?.unread_mention_count ?? 0,
});

export const resolveStudyGroupUnreadTotals = (
  group: StudyGroupUnreadGroupLike,
  isActiveStudyGroup: boolean,
  activeChannelTotals?: StudyGroupUnreadTotals,
  activeCatchUpTotals?: StudyGroupUnreadTotals,
): StudyGroupUnreadTotals => {
  const groupTotals = toStudyGroupUnreadTotalsFromGroup(group);
  if (!isActiveStudyGroup) return groupTotals;
  const channelTotals = activeChannelTotals ?? groupTotals;
  if (!activeCatchUpTotals) return channelTotals;
  return {
    unreadPostCommentCount: Math.max(
      channelTotals.unreadPostCommentCount,
      activeCatchUpTotals.unreadPostCommentCount,
    ),
    unreadMentions: Math.max(
      channelTotals.unreadMentions,
      activeCatchUpTotals.unreadMentions,
    ),
  };
};

export const toStudyGroupUnreadTotalsFromCatchUp = (
  items: StudyGroupCatchUpUnreadLike[],
): StudyGroupUnreadTotals => ({
  unreadPostCommentCount: items.length,
  unreadMentions: items.filter((item) => item.isMention).length,
});

export const countOffscreenUnreadGroups = (
  groups: OffscreenUnreadGroupRect[],
  viewport: OffscreenUnreadViewport,
) =>
  groups.reduce(
    (counts, group) => {
      if (!group.hasUnread) return counts;
      if (group.bottom < viewport.top) counts.above += 1;
      else if (group.top > viewport.bottom) counts.below += 1;
      return counts;
    },
    { above: 0, below: 0 },
  );

export const toStudyGroupUnreadTotalsFromResponse = (
  response?: StudyGroupUnreadTotalsResponseLike | null,
): StudyGroupUnreadTotals => ({
  unreadPostCommentCount: response?.unread_post_comment_count_total ?? 0,
  unreadMentions: response?.unread_mention_count_total ?? 0,
});

export const applyStudyGroupUnreadTotalsToGroup = <
  T extends StudyGroupUnreadGroupLike,
>(
  group: T,
  totals: StudyGroupUnreadTotals,
): T => ({
  ...group,
  unread_post_comment_count: totals.unreadPostCommentCount,
  unread_mention_count: totals.unreadMentions,
});

export const replaceStudyGroupUnreadTotalsInAggregate = (
  aggregate: StudyGroupUnreadTotals,
  previousGroupTotals: StudyGroupUnreadTotals,
  nextGroupTotals: StudyGroupUnreadTotals,
): StudyGroupUnreadTotals => ({
  unreadPostCommentCount: Math.max(
    0,
    aggregate.unreadPostCommentCount -
      previousGroupTotals.unreadPostCommentCount +
      nextGroupTotals.unreadPostCommentCount,
  ),
  unreadMentions: Math.max(
    0,
    aggregate.unreadMentions -
      previousGroupTotals.unreadMentions +
      nextGroupTotals.unreadMentions,
  ),
});

export const deriveStudyGroupUnreadTotalsFromChannels = (
  channelUnreadCounts: StudyGroupChannelUnreadLike[],
): StudyGroupUnreadTotals => {
  return channelUnreadCounts.reduce<StudyGroupUnreadTotals>(
    (acc, channelUnread) => {
      acc.unreadPostCommentCount +=
        channelUnread.unread_post_comment_count ?? 0;
      acc.unreadMentions += channelUnread.unread_mention_count ?? 0;
      return acc;
    },
    { ...EMPTY_STUDY_GROUP_UNREAD_TOTALS },
  );
};
